import React from 'react';
import T from 'i18n';
import {
  Box,
  Button,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  IconButton,
  CircularProgress,
  Typography,
  Checkbox,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { selectFolderContainers, selectFolders } from 'common/selectors';
import { fetchContainers } from 'features/case/redux/actions';
import { removeIndexFolders } from 'utils/foldersStructuring';
import api from 'common/api';
import type { FileItem } from './FilesSelectionPanel';

function OverflowTooltip({ children, title }: { children: React.ReactElement; title: string }) {
  const [isOverflowed, setIsOverflowed] = React.useState(false);
  const textRef = React.useRef<HTMLElement | null>(null);

  const checkOverflow = React.useCallback(() => {
    const el = textRef.current;
    if (el) {
      const candidates = el.querySelectorAll('p, span, div');
      let overflowed = false;
      candidates.forEach((child) => {
        if (child.scrollWidth > child.clientWidth) {
          overflowed = true;
        }
      });
      if (el.scrollWidth > el.clientWidth) {
        overflowed = true;
      }
      setIsOverflowed(overflowed);
    }
  }, []);

  return (
    <Tooltip title={isOverflowed ? title : ''} enterDelay={300}>
      <Box ref={textRef} onMouseEnter={checkOverflow} sx={{ minWidth: 0, flex: 1 }}>
        {children}
      </Box>
    </Tooltip>
  );
}

export interface BundleSelection {
  id: string;
  name: string;
}

type AllowedMode = 'files' | 'bundles' | 'both';

interface FilesBundlesBrowsePanelProps {
  onFileSelect: (file: FileItem) => void;
  selectedFiles: FileItem[];
  onBundleSelect: (bundle: BundleSelection) => void;
  onBundleDeselect: (bundle: BundleSelection) => void;
  selectedBundles: BundleSelection[];
  selectionMode: 'none' | 'files' | 'bundles';
  caseId: string;
  allowedMode?: AllowedMode;
  onClearTab?: (leafIds: Set<string>) => void;
  onToggleAllFolderFiles?: (files: FileItem[], folderId: string, selectAll: boolean) => void;
  compareMode?: boolean;
}

interface FolderNode {
  id: string;
  name: string;
  code: string;
  folders?: FolderNode[];
  fileCount?: number;
  [key: string]: any;
}

interface ContainerData {
  id: string;
  name: string;
  code: string;
  folders: FolderNode[];
  [key: string]: any;
}

interface BundleLocation {
  folderId: string;
  fileName: string;
  containerType: string;
  pathName: string;
  path: string;
}

interface DocFinderFileResult {
  id: string;
  name: string;
  originalName?: string;
  pageCount?: string;
  type?: string;
  source?: string;
  bundleLocations: BundleLocation[];
}

interface DocFinderResponse {
  count: number;
  results: DocFinderFileResult[];
  error?: string;
}

interface SearchResultFolder {
  folderId: string;
  folderName: string;
  files: FileItem[];
}

const CONTAINER_CODES = ['trialbooks', 'teamBundles', 'privateBundles'];

const CONTAINER_SEARCH_PARAM: Record<string, string> = {
  trialbooks: 'trialbooks',
  teamBundles: 'team',
  privateBundles: 'private',
};

const PAGE_SIZE = 500;

interface FolderTreeItemProps {
  folder: FolderNode;
  level: number;
  caseId: string;
  containerCode: string;
  onFileSelect: (file: FileItem) => void;
  selectedFiles: FileItem[];
  onBundleSelect: (bundle: BundleSelection) => void;
  onBundleDeselect: (bundle: BundleSelection) => void;
  selectedBundles: BundleSelection[];
  selectionMode: 'none' | 'files' | 'bundles';
  allowedMode: AllowedMode;
  filesCache: React.MutableRefObject<Map<string, FileItem[]>>;
  bundleSearchQuery?: string;
  onToggleAllFolderFiles?: (files: FileItem[], folderId: string, selectAll: boolean) => void;
  compareMode?: boolean;
}

function isBundleSelected(bundleId: string, selectedBundles: BundleSelection[]): boolean {
  return selectedBundles.some((b) => String(b.id) === String(bundleId));
}

/** Recursively collect all leaf folder IDs from a list of folder nodes. */
function collectLeafIds(nodes: FolderNode[]): Set<string> {
  const ids = new Set<string>();
  const visit = (node: FolderNode) => {
    if (node.folders && node.folders.length > 0) {
      node.folders.forEach(visit);
    } else {
      ids.add(node.id);
    }
  };
  nodes.forEach(visit);
  return ids;
}

/** Recursively collect all non-empty leaf bundles from a folder node. */
function getLeafBundles(node: FolderNode): BundleSelection[] {
  const hasChildren = node.folders && node.folders.length > 0;
  if (!hasChildren) {
    // Leaf node — include only if it has files
    if (node.fileCount === 0) return [];
    return [{ id: node.id, name: node.name }];
  }
  const leaves: BundleSelection[] = [];
  for (const child of node.folders!) {
    leaves.push(...getLeafBundles(child));
  }
  return leaves;
}

function FolderTreeItem({
  folder,
  level,
  caseId,
  containerCode,
  onFileSelect,
  selectedFiles,
  onBundleSelect,
  onBundleDeselect,
  selectedBundles,
  selectionMode,
  allowedMode,
  filesCache,
  bundleSearchQuery = '',
  onToggleAllFolderFiles,
  compareMode = false,
}: FolderTreeItemProps) {
  const { addToast } = useToasts();
  const [expanded, setExpanded] = React.useState(false);
  const [files, setFiles] = React.useState<FileItem[] | null>(null);
  const [loadingFiles, setLoadingFiles] = React.useState(false);
  const [transitioning, setTransitioning] = React.useState(false);
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  const hasSubFolders = folder.folders && folder.folders.length > 0;

  // Auto-expand when bundle search matches children but not this node itself
  React.useEffect(() => {
    if (!bundleSearchQuery || !hasSubFolders) return;
    const selfMatches = String(folder.name ?? '')
      .toLowerCase()
      .includes(bundleSearchQuery);
    if (!selfMatches) {
      setExpanded(true);
    }
  }, [bundleSearchQuery, folder.name, hasSubFolders]);

  // Collapse back when bundle search is cleared
  const prevQueryRef = React.useRef(bundleSearchQuery);
  React.useEffect(() => {
    if (prevQueryRef.current && !bundleSearchQuery) {
      setExpanded(false);
    }
    prevQueryRef.current = bundleSearchQuery;
  }, [bundleSearchQuery]);

  // A leaf bundle has no sub-folders — it only contains files.
  const isLeafBundle = !hasSubFolders;
  const isEmptyLeaf = isLeafBundle && folder.fileCount === 0;
  const showBundleCheckbox = selectionMode !== 'files' && allowedMode !== 'files' && !isEmptyLeaf;
  const isBundleMode = selectionMode === 'bundles' || allowedMode === 'bundles';
  const showFileCheckbox = !isBundleMode;
  const fileClickDisabled = isBundleMode;
  // A leaf bundle is expandable only when it has files.
  // Bundles with 0 files (fileCount === 0) should not be expandable.
  const hasFiles = folder.fileCount == null || folder.fileCount > 0;
  const isExpandable = hasSubFolders || (isLeafBundle && hasFiles);

  const handleToggle = async () => {
    if (!isExpandable) {
      if (isEmptyLeaf) {
        addToast(T.translate('generic.emptyBundleCannotBeSelected'), {
          appearance: 'info',
          autoDismiss: true,
        });
      }
      return;
    }

    const willExpand = !expanded;

    if (!willExpand) {
      setTransitioning(true);
      setVisibleCount(0);
      setTimeout(() => {
        setExpanded(false);
        setTransitioning(false);
        setVisibleCount(PAGE_SIZE);
      }, 0);
      return;
    }

    setExpanded(true);
    setTransitioning(true);

    if (files === null && !loadingFiles) {
      const cached = filesCache.current.get(folder.id);
      if (cached) {
        setFiles(cached);
        setTimeout(() => setTransitioning(false), 0);
        return;
      }

      setLoadingFiles(true);
      try {
        const response: any = await api.get(`/cases/${caseId}/folders/${folder.id}/files`);
        if (response && !response.error) {
          const mapped: FileItem[] = (Array.isArray(response) ? response : []).map((f: any) => ({
            id: f.id,
            name: f.name || f.folderSpecificName || '',
            pageCount: f.pageCount != null ? String(f.pageCount) : undefined,
            type: f.type,
            originalName: f.originalName,
            globalPagePrefix: f.globalPagePrefix,
            tab: f.tab != null ? String(f.tab) : undefined,
          }));
          filesCache.current.set(folder.id, mapped);
          setFiles(mapped);
        } else {
          setFiles([]);
        }
      } catch {
        setFiles([]);
      } finally {
        setLoadingFiles(false);
        setTransitioning(false);
      }
    } else {
      setTimeout(() => setTransitioning(false), 0);
    }
  };

  const leafBundles = React.useMemo(() => getLeafBundles(folder), [folder]);
  const selectedLeafCount = React.useMemo(
    () => leafBundles.filter((lb) => isBundleSelected(lb.id, selectedBundles)).length,
    [leafBundles, selectedBundles],
  );
  const isThisBundleSelected = isLeafBundle
    ? isBundleSelected(folder.id, selectedBundles)
    : selectedLeafCount > 0 && selectedLeafCount === leafBundles.length;
  const isIndeterminate =
    !isLeafBundle && selectedLeafCount > 0 && selectedLeafCount < leafBundles.length;

  const handleBundleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLeafBundle) {
      const bundle: BundleSelection = { id: folder.id, name: folder.name };
      if (isThisBundleSelected) {
        onBundleDeselect(bundle);
      } else {
        onBundleSelect(bundle);
      }
    } else {
      // Parent folder: toggle all leaf bundles
      if (isThisBundleSelected || isIndeterminate) {
        // Deselect all leaf bundles
        for (const lb of leafBundles) {
          if (isBundleSelected(lb.id, selectedBundles)) {
            onBundleDeselect(lb);
          }
        }
      } else {
        // Select all leaf bundles
        for (const lb of leafBundles) {
          if (!isBundleSelected(lb.id, selectedBundles)) {
            onBundleSelect(lb);
          }
        }
      }
    }
  };
  const hasSelectedFiles = React.useMemo(() => {
    if (selectedFiles.length === 0) return false;
    if (isLeafBundle) {
      return selectedFiles.some((sf) => sf.folderId === folder.id);
    }
    // Parent folder: check if any selected file belongs to a descendant leaf
    const leafIds = new Set(leafBundles.map((lb) => lb.id));
    return selectedFiles.some((sf) => sf.folderId && leafIds.has(sf.folderId));
  }, [selectedFiles, folder.id, isLeafBundle, leafBundles]);
  const isHighlighted = isThisBundleSelected || isIndeterminate || hasSelectedFiles;

  const allFolderFilesSelected = React.useMemo(() => {
    if (!files || files.length === 0) return false;
    return files.every((f) => selectedFiles.some((sf) => sf.id === f.id));
  }, [files, selectedFiles]);

  const someFolderFilesSelected = React.useMemo(() => {
    if (!files || files.length === 0) return false;
    const count = files.filter((f) => selectedFiles.some((sf) => sf.id === f.id)).length;
    return count > 0 && count < files.length;
  }, [files, selectedFiles]);

  return (
    <>
      <ListItemButton
        onClick={handleToggle}
        sx={{
          pl: 2 + level * 2,
          py: 0.5,
          backgroundColor: isHighlighted ? '#d3dcf8' : 'transparent',
          '&:hover': { backgroundColor: '#eef2ff' },
        }}
      >
        {isExpandable ? (
          <IconButton size="small" sx={{ p: 0, mr: 0.5, flexShrink: 0 }}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 24, mr: 0.5, flexShrink: 0 }} />
        )}
        {showBundleCheckbox && (
          <Checkbox
            size="small"
            checked={isThisBundleSelected}
            indeterminate={isIndeterminate}
            onClick={handleBundleCheckboxClick}
            tabIndex={-1}
            disableRipple
            sx={{
              p: 0,
              mr: 0.5,
              color: 'rgba(0,0,0,0.3)',
              '&.Mui-checked': { color: '#4567E5' },
              '&.MuiCheckbox-indeterminate': { color: '#4567E5' },
            }}
          />
        )}
        <FolderIcon fontSize="small" sx={{ color: '#0E172A', mr: 1, flexShrink: 0 }} />
        <OverflowTooltip title={folder.name}>
          <ListItemText
            primary={folder.name}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: 500,
              noWrap: true,
              sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
            sx={{ minWidth: 0 }}
          />
        </OverflowTooltip>
        {hasSubFolders ? (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>
            {folder.folders!.length}{' '}
            {folder.folders!.length === 1
              ? T.translate('generic.bundle')
              : T.translate('generic.bundles')}
          </Typography>
        ) : (
          folder.fileCount != null && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>
              {folder.fileCount} {T.translate('case.files')}
            </Typography>
          )
        )}
        {isHighlighted && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#4667e5',
              flexShrink: 0,
              mr: 1,
            }}
          />
        )}
      </ListItemButton>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {/* Sub-folders */}
        {hasSubFolders &&
          folder.folders!.map((subFolder) => (
            <FolderTreeItem
              key={subFolder.id}
              folder={subFolder}
              level={level + 1}
              caseId={caseId}
              containerCode={containerCode}
              onFileSelect={onFileSelect}
              selectedFiles={selectedFiles}
              onBundleSelect={onBundleSelect}
              onBundleDeselect={onBundleDeselect}
              selectedBundles={selectedBundles}
              selectionMode={selectionMode}
              allowedMode={allowedMode}
              filesCache={filesCache}
              bundleSearchQuery={bundleSearchQuery}
              onToggleAllFolderFiles={onToggleAllFolderFiles}
              compareMode={compareMode}
            />
          ))}

        {/* Loading / Closing indicator */}
        {(loadingFiles || transitioning) && (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 6 + level * 2, py: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {!expanded || visibleCount === 0
                ? T.translate('generic.closingFolder')
                : T.translate('generic.loadingFiles')}
            </Typography>
          </Box>
        )}

        {/* Files list (paginated) */}
        {files && files.length > 0 && !transitioning && (
          <List disablePadding>
            {showFileCheckbox && onToggleAllFolderFiles && !compareMode && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pl: 6 + level * 2,
                  pr: 1,
                  py: 0.5,
                  minHeight: 36,
                }}
              >
                <ListItemButton
                  onClick={() => onToggleAllFolderFiles(files!, folder.id, !allFolderFilesSelected)}
                  disabled={fileClickDisabled}
                  sx={{
                    py: 0,
                    px: 0,
                    flex: 1,
                    minHeight: 'unset',
                    '&:hover': {
                      backgroundColor: fileClickDisabled ? 'transparent' : '#eef2ff',
                    },
                    '&.Mui-disabled': { opacity: 1 },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={allFolderFilesSelected}
                    indeterminate={someFolderFilesSelected}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      p: 0,
                      mr: 0.5,
                      color: 'rgba(0,0,0,0.3)',
                      '&.Mui-checked': { color: '#4567E5' },
                      '&.MuiCheckbox-indeterminate': { color: '#4567E5' },
                    }}
                  />
                  <Typography variant="body2" fontSize="11px" sx={{ color: '#4567E5' }}>
                    {allFolderFilesSelected
                      ? T.translate('generic.deselectAll')
                      : T.translate('generic.selectAllFiles')}
                  </Typography>
                </ListItemButton>
                {someFolderFilesSelected && !allFolderFilesSelected ? (
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleAllFolderFiles(files!, folder.id, false);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '11px',
                      flexShrink: 0,
                      ml: 0.5,
                      minWidth: 'unset',
                      p: 0,
                    }}
                  >
                    {T.translate('generic.clear')}
                  </Button>
                ) : null}
              </Box>
            )}
            {files.slice(0, visibleCount).map((file) => {
              const isSelected = !fileClickDisabled && selectedFiles.some((f) => f.id === file.id);
              return (
                <ListItemButton
                  key={file.id}
                  onClick={
                    fileClickDisabled
                      ? undefined
                      : () => onFileSelect({ ...file, folderId: folder.id })
                  }
                  disabled={fileClickDisabled}
                  sx={{
                    pl: 6 + level * 2,
                    py: 0.25,
                    backgroundColor: isSelected ? '#d3dcf8' : 'transparent',
                    '&:hover': { backgroundColor: fileClickDisabled ? 'transparent' : '#eef2ff' },
                    '&.Mui-disabled': { opacity: 1 },
                  }}
                >
                  {showFileCheckbox && (
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                      sx={{
                        p: 0,
                        mr: 0.5,
                        color: 'rgba(0,0,0,0.3)',
                        '&.Mui-checked': { color: '#4567E5' },
                      }}
                    />
                  )}
                  <FileIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                  <OverflowTooltip title={file.name}>
                    <ListItemText
                      primary={file.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        fontSize: '0.8125rem',
                        sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                      }}
                      sx={{ minWidth: 0 }}
                    />
                  </OverflowTooltip>
                  {(file.globalPagePrefix || file.tab) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1, flexShrink: 0 }}
                    >
                      {file.globalPagePrefix && file.tab
                        ? `${file.globalPagePrefix}/${file.tab}`
                        : file.globalPagePrefix || file.tab}
                    </Typography>
                  )}
                  {file.pageCount && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1, flexShrink: 0 }}
                    >
                      {file.pageCount}p
                    </Typography>
                  )}
                </ListItemButton>
              );
            })}
            {files.length > visibleCount && (
              <ListItemButton
                onClick={(e) => {
                  e.stopPropagation();
                  setVisibleCount((prev) => prev + PAGE_SIZE);
                }}
                sx={{
                  pl: 6 + level * 2,
                  py: 0.75,
                  justifyContent: 'center',
                  '&:hover': { backgroundColor: '#eef2ff' },
                }}
              >
                <Typography variant="caption" color="primary" fontWeight={600}>
                  Load more ({files.length - visibleCount} remaining)
                </Typography>
              </ListItemButton>
            )}
          </List>
        )}

        {/* No files message */}
        {files && files.length === 0 && !loadingFiles && !hasSubFolders && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ pl: 6 + level * 2, py: 1, display: 'block' }}
          >
            {T.translate('generic.noFilesInFolder')}
          </Typography>
        )}
      </Collapse>
    </>
  );
}

interface SearchResultItemProps {
  folder: SearchResultFolder;
  onFileSelect: (file: FileItem) => void;
  selectedFiles: FileItem[];
  selectionMode: 'none' | 'files' | 'bundles';
}

function SearchResultItem({
  folder,
  onFileSelect,
  selectedFiles,
  selectionMode,
}: SearchResultItemProps) {
  const showFileCheckbox = selectionMode !== 'bundles';
  const hasSelectedFiles = selectedFiles.some((sf) => sf.folderId === folder.folderId);

  return (
    <>
      <ListItemButton
        sx={{
          pl: 2,
          py: 0.5,
          cursor: 'default',
          backgroundColor: hasSelectedFiles ? '#d3dcf8' : 'transparent',
          '&:hover': { backgroundColor: '#eef2ff' },
        }}
        disableRipple
      >
        <FolderIcon fontSize="small" sx={{ color: '#0E172A', mr: 1, flexShrink: 0 }} />
        <OverflowTooltip title={folder.folderName}>
          <ListItemText
            primary={folder.folderName}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: 500,
              noWrap: true,
              sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
            sx={{ minWidth: 0 }}
          />
        </OverflowTooltip>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>
          {folder.files.length} {T.translate('case.files')}
        </Typography>
      </ListItemButton>

      <List disablePadding>
        {folder.files.map((file) => {
          const isSelected = selectedFiles.some((f) => f.id === file.id);
          return (
            <ListItemButton
              key={file.id}
              onClick={() => onFileSelect({ ...file, folderId: folder.folderId })}
              sx={{
                pl: 4,
                py: 0.25,
                backgroundColor: isSelected ? '#d3dcf8' : 'transparent',
                '&:hover': { backgroundColor: '#eef2ff' },
              }}
            >
              {showFileCheckbox ? (
                <Checkbox
                  size="small"
                  checked={isSelected}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    p: 0,
                    mr: 0.5,
                    color: 'rgba(0,0,0,0.3)',
                    '&.Mui-checked': { color: '#4567E5' },
                  }}
                />
              ) : null}
              <FileIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
              <OverflowTooltip title={file.name}>
                <ListItemText
                  primary={file.name}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: true,
                    fontSize: '0.8125rem',
                    sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                  sx={{ minWidth: 0 }}
                />
              </OverflowTooltip>
              {file.pageCount ? (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                  {file.pageCount}p
                </Typography>
              ) : null}
            </ListItemButton>
          );
        })}
      </List>
    </>
  );
}

/** Recursively filter folder tree by name (case-insensitive). */
function filterFolderTree(items: FolderNode[], query: string): FolderNode[] {
  if (!query) return items;
  const visit = (node: FolderNode): FolderNode | null => {
    const name = String(node?.name ?? '').toLowerCase();
    const matchesSelf = name.includes(query);
    const children = Array.isArray(node?.folders) ? node.folders : null;
    if (!children || children.length === 0) {
      return matchesSelf ? node : null;
    }
    const filteredChildren = children.map(visit).filter(Boolean) as FolderNode[];
    if (matchesSelf || filteredChildren.length > 0) {
      return { ...node, folders: filteredChildren };
    }
    return null;
  };
  return items.map(visit).filter(Boolean) as FolderNode[];
}

export default function FilesBundlesBrowsePanel({
  onFileSelect,
  selectedFiles,
  onBundleSelect,
  onBundleDeselect,
  selectedBundles,
  selectionMode,
  caseId,
  allowedMode = 'both',
  onClearTab,
  onToggleAllFolderFiles,
  compareMode = false,
}: FilesBundlesBrowsePanelProps) {
  const dispatch = useDispatch();
  const containers = useSelector(selectFolderContainers) as ContainerData[];
  const folders = useSelector(selectFolders) as Record<string, FolderNode[]>;
  const filesCache = React.useRef<Map<string, FileItem[]>>(new Map());
  const [searchText, setSearchText] = React.useState('');
  const [activeTab, setActiveTab] = React.useState(0);
  const [searchResults, setSearchResults] = React.useState<DocFinderFileResult[] | null>(null);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState(false);
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = React.useRef(0);
  const visibleContainers = React.useMemo(
    () => containers.filter((c) => CONTAINER_CODES.includes(c.code)),
    [containers],
  );

  React.useEffect(() => {
    if (containers.length === 0) {
      dispatch(fetchContainers());
    }
  }, [containers.length, dispatch]);

  const activeContainer = visibleContainers[activeTab] || visibleContainers[0];

  const containerFoldersMap = React.useMemo(() => {
    const map: Record<string, FolderNode[]> = {};
    for (const container of visibleContainers) {
      let baseFolders = folders[container.code] || container.folders || [];
      if (container.code === 'trialbooks') {
        baseFolders = removeIndexFolders(baseFolders) || [];
      }
      map[container.code] = baseFolders;
    }
    return map;
  }, [visibleContainers, folders]);

  const activeContainerCode = activeContainer?.code || '';

  // Track which containers have selections (files or bundles)
  const containerHasSelections = React.useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const container of visibleContainers) {
      const leafIds = collectLeafIds(containerFoldersMap[container.code] || []);
      const hasFiles = selectedFiles.some((sf) => sf.folderId && leafIds.has(sf.folderId));
      const hasBundles = selectedBundles.some((b) => leafIds.has(String(b.id)));
      result[container.code] = hasFiles || hasBundles;
    }
    return result;
  }, [visibleContainers, containerFoldersMap, selectedFiles, selectedBundles]);

  const handleClearActiveTab = React.useCallback(() => {
    if (!onClearTab || !activeContainerCode) return;
    const leafIds = collectLeafIds(containerFoldersMap[activeContainerCode] || []);
    onClearTab(leafIds);
  }, [onClearTab, activeContainerCode, containerFoldersMap]);

  // Debounced doc-finder API search when >= 3 characters
  React.useEffect(() => {
    const trimmed = searchText.trim();

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }

    if (trimmed.length < 3) {
      setSearchResults(null);
      setSearchLoading(false);
      setSearchError(false);
      searchRequestIdRef.current++;
      return;
    }

    setSearchLoading(true);
    setSearchError(false);
    searchTimerRef.current = setTimeout(async () => {
      const requestId = ++searchRequestIdRef.current;
      try {
        const containerParam = CONTAINER_SEARCH_PARAM[activeContainerCode] || 'trialbooks';
        const endpoint = `/cases/${caseId}/doc-finder?container=${containerParam}&search=${encodeURIComponent(trimmed)}`;
        const response = (await api.get(endpoint)) as DocFinderResponse;
        if (requestId !== searchRequestIdRef.current) return;

        if (response && !response.error && response.results) {
          const filtered = response.results.filter(
            (f: DocFinderFileResult) => f.source !== 'merge',
          );
          setSearchResults(filtered);
          setSearchError(false);
        } else {
          setSearchResults([]);
          setSearchError(Boolean(response?.error));
        }
      } catch {
        if (requestId !== searchRequestIdRef.current) return;
        setSearchResults([]);
        setSearchError(true);
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearchLoading(false);
        }
      }
    }, 400);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchText, caseId, activeContainerCode]);

  // Group search results by folder
  const searchResultFolders: SearchResultFolder[] = React.useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];

    const folderMap = new Map<string, SearchResultFolder>();

    for (const result of searchResults) {
      const fileItem: FileItem = {
        id: result.id,
        name: result.name,
        pageCount: result.pageCount != null ? String(result.pageCount) : undefined,
        type: result.type,
        originalName: result.originalName,
      };

      if (!result.bundleLocations || result.bundleLocations.length === 0) continue;

      for (const loc of result.bundleLocations) {
        const existing = folderMap.get(loc.folderId);
        if (existing) {
          if (!existing.files.some((f) => f.id === fileItem.id)) {
            existing.files.push(fileItem);
          }
        } else {
          folderMap.set(loc.folderId, {
            folderId: loc.folderId,
            folderName: loc.pathName || loc.fileName,
            files: [fileItem],
          });
        }
      }
    }

    return Array.from(folderMap.values());
  }, [searchResults]);

  // Populate filesCache with search results for later browsing
  React.useEffect(() => {
    for (const folder of searchResultFolders) {
      if (!filesCache.current.has(folder.folderId)) {
        filesCache.current.set(folder.folderId, folder.files);
      }
    }
  }, [searchResultFolders]);

  const isSearchActive = searchResults !== null || searchLoading;
  const hasSearchResults = searchResults !== null && searchResults.length > 0 && !searchLoading;

  // Client-side bundle name filtering (always active from 1st char)
  const bundleSearchQuery = searchText.trim().toLowerCase();
  const filteredContainerFoldersMap = React.useMemo(() => {
    if (!bundleSearchQuery) return containerFoldersMap;
    const map: Record<string, FolderNode[]> = {};
    for (const code of Object.keys(containerFoldersMap)) {
      map[code] = filterFolderTree(containerFoldersMap[code], bundleSearchQuery);
    }
    return map;
  }, [containerFoldersMap, bundleSearchQuery]);

  if (visibleContainers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          {T.translate('generic.loadingFolders')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Container Tabs — hidden during search */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          setActiveTab(newValue);
          setSearchText('');
        }}
        variant="standard"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          '& .MuiTabs-flexContainer': {
            justifyContent: 'flex-start',
            gap: '4px',
            pl: 0,
          },
          '& .MuiTab-root': {
            minHeight: 36,
            minWidth: 'auto',
            textTransform: 'none',
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: '#64748b',
            padding: '6px 18px',
            transition: 'color 200ms ease',
            '&:hover': {
              color: '#334155',
            },
          },
          '& .Mui-selected': {
            color: '#4567E5 !important',
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#4567E5',
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        }}
      >
        {visibleContainers.map((container) => (
          <Tab
            key={container.code}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {container.name}
                {containerHasSelections[container.code] && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#4667e5',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {/* Search input below tabs */}
      <Box sx={{ px: 1, py: 1 }}>
        <TextField
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={T.translate('generic.searchBundlesOrFiles') as string}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchText('')} edge="end" size="small">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '5px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d1d5db',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999999',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999999',
              borderWidth: '1px',
            },
          }}
        />
      </Box>

      {/* Hint message + Clear All — combined row */}
      {(allowedMode === 'both' || (onClearTab && containerHasSelections[activeContainerCode])) && (
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {allowedMode === 'both' ? (
            <Typography variant="caption" sx={{ fontSize: '11px', color: '#4667e5' }}>
              {T.translate('generic.chooseBundlesOrFiles')}
            </Typography>
          ) : (
            <Box />
          )}
          {onClearTab && containerHasSelections[activeContainerCode] ? (
            <Button
              size="small"
              onClick={handleClearActiveTab}
              sx={{ textTransform: 'none', fontSize: '11px', flexShrink: 0 }}
            >
              {T.translate('generic.clearAll')}
            </Button>
          ) : null}
        </Box>
      )}

      {/* File search status bar — loading or error */}
      {searchLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75 }}>
          <CircularProgress size={14} sx={{ mr: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {T.translate('generic.searchingFiles')}
          </Typography>
        </Box>
      ) : isSearchActive && searchError ? (
        <Box sx={{ px: 1.5, py: 0.75 }}>
          <Typography variant="caption" color="error">
            {T.translate('case.searchError')}
          </Typography>
        </Box>
      ) : null}

      {/* Scrollable area for file search results + browse trees */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {/* File search results */}
        {isSearchActive && !searchLoading && searchResultFolders.length > 0 ? (
          <Box sx={{ mt: 0.5 }}>
            <List disablePadding>
              {searchResultFolders.map((folder) => (
                <SearchResultItem
                  key={folder.folderId}
                  folder={folder}
                  onFileSelect={onFileSelect}
                  selectedFiles={selectedFiles}
                  selectionMode={selectionMode}
                />
              ))}
            </List>
          </Box>
        ) : null}

        {/* Browse trees — all tabs always rendered, only active shown, to preserve expanded state */}
        {visibleContainers.map((container) => {
          const isActive = container.code === activeContainer?.code;
          const containerFolders = filteredContainerFoldersMap[container.code] || [];
          return (
            <Box
              key={container.code}
              sx={{
                mt: 0.5,
                display: isActive ? 'block' : 'none',
              }}
            >
              {containerFolders.length === 0 ? (
                !hasSearchResults ? (
                  <Box sx={{ px: 1.5, py: 0.75 }}>
                    <Typography
                      variant="caption"
                      sx={
                        bundleSearchQuery
                          ? { fontSize: '11px', color: '#4667e5' }
                          : { color: 'text.secondary' }
                      }
                    >
                      {bundleSearchQuery
                        ? T.translate('generic.noResultsFound')
                        : T.translate('generic.noFoldersAvailable')}
                    </Typography>
                  </Box>
                ) : null
              ) : (
                <List disablePadding>
                  {containerFolders.map((folder: FolderNode) => (
                    <FolderTreeItem
                      key={folder.id}
                      folder={folder}
                      level={0}
                      caseId={caseId}
                      containerCode={container.code}
                      onFileSelect={onFileSelect}
                      selectedFiles={selectedFiles}
                      onBundleSelect={onBundleSelect}
                      onBundleDeselect={onBundleDeselect}
                      selectedBundles={selectedBundles}
                      selectionMode={selectionMode}
                      allowedMode={allowedMode}
                      filesCache={filesCache}
                      bundleSearchQuery={bundleSearchQuery}
                      onToggleAllFolderFiles={onToggleAllFolderFiles}
                      compareMode={compareMode}
                    />
                  ))}
                </List>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
